/**
 * @file parleBarBundle.js
 * @description Bar-mic Product Family resolution — 'Parlé' vs 'Devio' (UCX).
 *
 * A "single" bar mic design (CBC-2500, ABC-2500, VBC-2500a, or VBC-2800 — i.e.
 * any `MIC_CATEGORIES.BAR` model — with NO expansion mics/speakers added)
 * resolves Product Family as follows:
 *   - Windows (or platform unset), with or without UC Compute selected → 'Parlé'.
 *   - Android + UC Compute (Microsoft Teams or Zoom Rooms) selected → 'Devio'
 *     (the Devio UCX / Tree Frog hub + touch console bundle).
 *   - Android + UC Compute = 'None' → 'Parlé' (no hub needed — the bar's own
 *     internal DSP/amp is the whole system).
 *
 * Bar-ness is derived from the microphone MODEL (`MIC_CATEGORIES.BAR`), the
 * same way `isDevioUcxEligible` (treeFrogPorts.js) determines it — not from
 * `area.hasBar` — so the two stay in agreement regardless of whether some
 * caller kept `hasBar` in sync.
 *
 * Bar + expansion mics/speakers is a DIFFERENT, pre-existing case: it makes
 * Devio UCX (Android) the only valid platform regardless of UC Compute (see
 * `isDevioUcxEligible`'s `exclusive` flag in treeFrogPorts.js) — that's owned
 * by `revalidateUcxEligibility` in reduxModules/ducks/room.ts, not here.
 * `resolveBarProductFamily` returns `null` (don't touch) for that shape so it
 * doesn't fight that forcing logic.
 */
import { MIC_CATEGORIES } from 'types/models/microphone';

/**
 * True when the area's primary microphone model is a bar (CBC-2500,
 * ABC-2500, VBC-2500a, VBC-2800) with no expansion mics/speakers added — a
 * "single" bar design, as opposed to bar + expansion (a different, exclusive
 * Devio-UCX-only case handled elsewhere).
 *
 * @param {Object|null|undefined} area
 * @returns {boolean}
 */
export const isSingleBarDesign = (area) => {
  const model = area?.microphones?.model;
  if (!model || !MIC_CATEGORIES.BAR.has(model)) return false;

  const hasExpansionMics = Boolean(area.expansionMicType) && area.expansionMicType !== 'none';
  const hasExpansionSpeakers = Boolean(area.expansionSpeakerType) && area.expansionSpeakerType !== 'none';
  return !hasExpansionMics && !hasExpansionSpeakers;
};

/**
 * Resolves what `area.productFamily` should be forced to for a "single" bar
 * mic design (see `isSingleBarDesign`):
 *   - Windows (or unset), any UC Compute → 'Parlé'.
 *   - Android + UC Compute (Teams/Zoom) → 'Devio'.
 *   - Android + UC Compute = 'None' → 'Parlé'.
 *
 * Returns `null` ("don't touch it") when:
 *   - The microphone isn't a bar model — clears a stale 'Parlé' left over
 *     from a prior bar configuration, otherwise leaves the real Devio/Tesira
 *     choice (ceiling/table mic designs) alone.
 *   - Bar + expansion mics/speakers — that's the exclusive Devio-UCX-only case
 *     owned by `revalidateUcxEligibility`'s exclusive branch, not this one.
 *
 * @param {Object|null|undefined} area
 * @returns {'Parlé'|'Devio'|'Tesira'|null}
 */
export const resolveBarProductFamily = (area) => {
  if (!area) return null;

  const model = area.microphones?.model;
  const isBarMic = Boolean(model) && MIC_CATEGORIES.BAR.has(model);

  if (!isBarMic) {
    // Not a bar design — 'Parlé' has no meaning once Family becomes a real
    // Devio/Tesira choice again, so clear a stale value left over from a
    // prior bar configuration. Otherwise leave the user's choice alone.
    return area.productFamily === 'Parlé' ? 'Tesira' : null;
  }

  if (!isSingleBarDesign(area)) {
    // Bar + expansion — the exclusive Tree Frog forcing logic in
    // revalidateUcxEligibility owns Family (always 'Devio') for this shape.
    return null;
  }

  const hasUCCompute = area.ucCompute === 'Microsoft Teams' || area.ucCompute === 'Zoom Rooms';
  if (area.platform === 'Android') {
    return hasUCCompute ? 'Devio' : 'Parlé';
  }

  // Windows (or unset) + a single bar, any UC Compute selection → Parlé.
  return 'Parlé';
};
